import { PropsWithChildren, Suspense, useEffect } from 'react';

import { QueryClientProvider } from '@tanstack/react-query';
// eslint-disable-next-line import/no-extraneous-dependencies
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider, Empty, Space, Spin } from 'antd';
import locale from 'antd/es/locale/pt_BR';
import theme from 'antd/lib/theme';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter as Router } from 'react-router-dom';

import { ErrorFallback } from '@/components/feedback/ErrorFallback';
import { IS_PRODUCTION } from '@/config/environment';
// eslint-disable-next-line import/order
import { queryClient } from '@/lib/react-query';

import 'moment/locale/pt-br';
import { useThemeStore } from '@/stores/useThemeStore';

export const AppProvider = ({ children }: PropsWithChildren<Record<string, unknown>>) => {
  const isDarkMode = useThemeStore((state) => state.theme === `dark`);

  useEffect(() => {
    if (isDarkMode) document.documentElement.setAttribute(`data-theme`, `dark`);
    else if (document.documentElement.hasAttribute(`data-theme`))
      document.documentElement.removeAttribute(`data-theme`);
  }, [isDarkMode]);

  return (
    <Suspense
      fallback={
        <Space align="center" className="u-flex u-justify-center u-w-full u-h-full">
          <Spin size="large" />
        </Space>
      }
    >
      <QueryClientProvider client={queryClient}>
        <ConfigProvider
          locale={locale}
          renderEmpty={() => (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nenhum resultado encontrado" />
          )}
          theme={{
            algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
            token: isDarkMode
              ? {
                  // AstriHub dark
                  fontFamily: `'Inter', system-ui, sans-serif`,
                  colorPrimary: `#00D865`,
                  colorLink: `#99EFC1`,
                  colorLinkHover: `#CCF7E0`,
                  colorSuccess: `#99EFC1`,
                  colorError: `#FDA29B`,
                  colorWarning: `#F4A261`,
                  colorInfo: `#CEDEE1`,
                  colorBgLayout: `#05292F`,
                  colorBgContainer: `#07373E`,
                  colorBgElevated: `#07373E`,
                  colorFillAlter: `#08444E`,
                  colorBorder: `#6D9DA4`,
                  colorBorderSecondary: `#0B5B68`,
                  colorSplit: `#0B5B68`,
                  colorText: `#FFFFFF`,
                  colorTextSecondary: `#CEDEE1`,
                  colorTextTertiary: `#CEDEE1`,
                  colorTextDescription: `#CEDEE1`,
                  colorTextPlaceholder: `#6D9DA4`,
                  colorTextDisabled: `#6D9DA4`,
                  colorBgContainerDisabled: `#08444E`,
                  borderRadius: 8,
                  borderRadiusLG: 12,
                }
              : {
                  fontFamily: `'Inter', system-ui, sans-serif`,
                  // Petróleo da marca (seções do site); o verde da marca fica nas ações (botão primário).
                  colorPrimary: `#0B5B68`,
                  colorSuccess: `#2d6a4f`,
                  colorError: `#cc3340`,
                  colorWarning: `#dda15e`,
                  borderRadiusLG: 14,
                },
            components: isDarkMode
              ? {
                  Button: {
                    colorPrimary: `#00D865`,
                    colorPrimaryHover: `#00A24C`,
                    colorPrimaryActive: `#00612D`,
                  },
                  Tabs: {
                    itemSelectedColor: `#99EFC1`,
                    itemHoverColor: `#CCF7E0`,
                    itemActiveColor: `#CCF7E0`,
                    inkBarColor: `#00D865`,
                  },
                  Menu: {
                    itemSelectedBg: `#08444E`,
                    itemSelectedColor: `#99EFC1`,
                    itemHoverBg: `#08444E`,
                  },
                }
              : {
                  Button: {
                    colorPrimary: `#00D865`,
                    colorPrimaryHover: `#10F35E`,
                    colorPrimaryActive: `#00B857`,
                  },
                },
          }}
        >
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            {!IS_PRODUCTION && <ReactQueryDevtools position="bottom-right" />}
            <Router>{children}</Router>
          </ErrorBoundary>
        </ConfigProvider>
      </QueryClientProvider>
    </Suspense>
  );
};
